import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer,
  AreaChart, Area, XAxis, Tooltip,
} from "recharts";
import {
  TrendingUp, TrendingDown, Brain, Sparkles, Check, Star, Calendar, Zap, Award, Flame, ArrowUpRight,
  PenTool, ChevronRight, Circle, CircleDot, CheckCircle2, Target, Rocket, ArrowRight, Building2,
} from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { ScoreRing, AnimatedNumber } from "../components/ScoreRing";
import { useApp } from "../context/AppContext";
import { useLang } from "../lib/i18n";
import { journeyT, getPriority, getWeakestSub } from "../lib/journey";
import { api } from "../lib/api";
import { SUB_LABELS, NOVA_DEMO, CAL_COLORS } from "../lib/demoData";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { MissionReminders } from "../components/MissionReminders";
import { HelpHint } from "../components/HelpHint";
import { Skeleton } from "../components/ui/skeleton";
import { useS } from "../lib/simple";

const stagger = { animate: { transition: { staggerChildren: 0.06 } } };
const item = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

const PRIO_COLOR = { haute: "#EF4444", moyenne: "#F59E0B", basse: "#22C55E" };
const PROV_LABEL_KEY = { saisi: "diag_declared", extrait: "diag_observed", calcul: "diag_calc" };
const STATUS_META = {
  todo: { label: "À faire", color: "#94A3B8", icon: Circle },
  progress: { label: "En cours", color: "#00E5FF", icon: CircleDot },
  done: { label: "Terminée", color: "#10B981", icon: CheckCircle2 },
};

function Card({ children, className = "", testid, span = "" }) {
  return (
    <motion.div variants={item} className={`lb-card lb-card-hover p-5 ${span} ${className}`} data-testid={testid}>
      {children}
    </motion.div>
  );
}

export default function Dashboard({ variant }) {
  const nav = useNavigate();
  const { s } = useS();
  const isResults = variant === "results";
  const { data, demo, toggleAction, setActionStatus, setPendingContent } = useApp();
  const { lang, t } = useLang();
  const J = journeyT(lang);
  const [selected, setSelected] = useState(null);
  const [prof360, setProf360] = React.useState(null);
  const [diag, setDiag] = React.useState(null);
  const [diagPlan, setDiagPlan] = React.useState(null);
  React.useEffect(() => {
    if (demo) return;
    api.get("/company-profile").then(({ data: d }) => setProf360(d?.completion ?? 0)).catch(() => {});
    api.get("/diagnostic").then(({ data: d }) => setDiag(d && d.diagnostic_id ? d : null)).catch(() => {});
    api.get("/action-plan").then(({ data: d }) => setDiagPlan(d && d.plan_id ? d : null)).catch(() => {});
  }, [demo]);

  if (!data || !data.scores) {
    return <Layout><div className="text-center py-20"><p className="text-[hsl(var(--muted-foreground))] mb-4">Aucune analyse disponible.</p><button onClick={() => nav("/onboarding")} className="text-neon" data-testid="go-onboarding">Analyser mon business →</button></div></Layout>;
  }

  const scores = data.scores;
  const radarData = Object.entries(scores.subs).map(([k, v]) => ({ subject: SUB_LABELS[k], value: v }));
  const allActions = data.actions || [];
  const planMissions = allActions.filter((a) => a.isMission);
  const hasPlan = planMissions.length > 0;
  // Plan is the primary source: once a plan exists, only its missions are shown.
  const actions = hasPlan ? planMissions : allActions;
  const scope = actions;
  const doneCount = scope.filter((a) => a.done).length;
  const planProgress = scope.length ? Math.round((doneCount / scope.length) * 100) : 0;

  // Real vs demo data: never invent commercial stats for real accounts.
  // Evolution curve requires ≥2 real dated snapshots; a single analysis shows the "initial" note.
  const realHistory = data.history && data.history.length >= 2 ? data.history : null;
  const history = realHistory || (demo ? NOVA_DEMO.history : null);
  const gam = data.gamification || (demo ? NOVA_DEMO.gamification : null);
  const rep = data.reputation || (demo ? NOVA_DEMO.reputation : null);
  const recent = data.recent || (demo ? NOVA_DEMO.recent : []);
  // Upcoming = the active plan's scheduled missions (publications/animations/promos), else demo calendar.
  const planCalendar = (data.calendar || []).filter((e) => e.missionId);
  const upcoming = hasPlan ? planCalendar : (demo ? (data.calendar || NOVA_DEMO.calendar) : []);
  const upcomingSorted = [...upcoming].sort((a, b) => a.day - b.day).slice(0, 4);
  const plannedCount = hasPlan ? planCalendar.length : (demo ? (data.calendar || NOVA_DEMO.calendar).length : 0);
  const scoreDelta = history ? scores.global - (history[0]?.score || scores.global) : null;
  const bizName = data.onboarding?.business_name || "Votre commerce";

  // Guided-journey helpers (jury demo): priority hero, plan-in-progress banner, score explainer.
  const priority = !hasPlan ? getPriority(data, lang) : null;
  const weak = getWeakestSub(data, lang);
  const nextMission = hasPlan
    ? [...planMissions.filter((m) => !m.done)].sort((a, b) => (a.day_offset ?? 99) - (b.day_offset ?? 99))[0]
    : null;
  const planObjective = data.plan?.objective;
  const goActWithAI = () => nav("/app/plan");
  const scrollToOpportunities = () => {
    document.querySelector('[data-testid="dash-actions"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // "Depuis votre dernière analyse" — real per-axis comparison (needs ≥2 snapshots with subs).
  const snaps = (data.history || []).filter((h) => h.subs);
  const canCompare = snaps.length >= 2;
  const prevSnap = canCompare ? snaps[snaps.length - 2] : null;
  const lastSnap = canCompare ? snaps[snaps.length - 1] : null;
  const compareRows = canCompare ? Object.keys(lastSnap.subs).map((k) => {
    const a = prevSnap.subs[k] ?? lastSnap.subs[k];
    const b = lastSnap.subs[k];
    return { k, label: SUB_LABELS[k], from: a, to: b, delta: b - a };
  }) : [];
  const topGain = compareRows.reduce((m, r) => (r.delta > (m?.delta || 0) ? r : m), null);

  // Map a diagnostic sub-score to the kind of deliverable the merchant can create for it.
  const SUB_TO_CT = { visibilite: "instagram", presence_digitale: "facebook", fidelisation: "whatsapp", reputation: "sms", animation: "animation", potentiel: "promotion" };
  const CTA_LABEL = { whatsapp: "Créer mon message", sms: "Créer mon SMS", email: "Créer mon email", promotion: "Créer ma promo", animation: "Préparer mon animation", review_reply: "Rédiger ma réponse" };

  const NR = <span className="text-[hsl(var(--muted-foreground))]">Non renseigné</span>;

  const prepareContent = (m) => {
    const ct = m.content_type || SUB_TO_CT[m.sub] || "instagram";
    setPendingContent({ content_type: ct, topic: m.title, why: m.why || "" });
    setSelected(null);
    nav("/app/content");
  };
  const canCreate = (m) => !!(m && (m.needs_content || m.sub || m.content_type));
  const ctaLabel = (m) => {
    const ct = m.content_type || SUB_TO_CT[m.sub] || "instagram";
    return CTA_LABEL[ct] || "Créer ma publication";
  };
  const changeStatus = (id, status) => {
    setActionStatus(id, status);
    setSelected((s) => (s ? { ...s, status, done: status === "done" } : s));
    if (status === "done") toast.success("Mission terminée ! 🎉");
  };

  return (
    <Layout>
      <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-4">
        {/* Header */}
        <motion.div variants={item} className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-[hsl(var(--muted-foreground))] text-sm">{isResults ? s("results_sub") : "Bonjour 👋"}</p>
            <h1 className="font-display font-black text-2xl lg:text-3xl" data-testid={isResults ? "results-title" : undefined}>{isResults ? s("results_title") : bizName}</h1>
          </div>
          {scoreDelta != null && scoreDelta > 0 && (
            <div className="flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              <TrendingUp size={16} /> Score en progression
            </div>
          )}
        </motion.div>

        {/* AGIR / PILOTER — le copilote est la porte d'entrée du quotidien (aucun prérequis) */}
        {!isResults && <motion.div variants={item} className="beam p-[1px]" data-testid="dash-today">
          <div className="rounded-[1.2rem] bg-[var(--lb-card)] p-5">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-neon/15 flex items-center justify-center text-neon shrink-0"><Sparkles size={22} /></div>
              <div className="min-w-0 flex-1">
                <h2 className="font-display font-black text-lg sm:text-xl leading-tight">{t("dash_today_title")}</h2>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{t("dash_today_sub")}</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <Button onClick={() => nav("/app/command")} data-testid="dash-today-cta" className="flex-1 rounded-full h-12 min-h-[48px] bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">
                <Sparkles size={17} className="mr-1.5" /> {t("dash_today_cta")}
              </Button>
              <Button onClick={() => nav("/app/diagnostic")} data-testid="dash-analyze-deep" variant="outline" className="rounded-full h-12 px-4 border-white/10 bg-white/5 text-sm">
                <Brain size={16} className="mr-1.5" /> {t("dash_analyze_deep")}
              </Button>
            </div>
          </div>
        </motion.div>}

        {!demo && prof360 === null && (
          <motion.div variants={item} className="lb-card p-5" data-testid="dash-pipeline-loading">
            <div className="flex items-center gap-2 mb-3"><Skeleton className="h-4 w-4 rounded" /><Skeleton className="h-4 w-44" /></div>
            <Skeleton className="h-2 w-full rounded-full mb-3" />
            <Skeleton className="h-4 w-3/4 mb-4" />
            <Skeleton className="h-11 w-full rounded-full" />
          </motion.div>
        )}

        {/* Compte neuf : carte d'accueil (aucune donnée, aucun appel IA) — disparaît dès profil entamé ou diagnostic */}
        {!demo && !isResults && prof360 === 0 && !diag && (
          <motion.div variants={item} className="lb-card p-5 border-neon/20" data-testid="dash-welcome">
            <div className="flex items-center gap-2 mb-2"><Sparkles size={18} className="text-neon" /><p className="font-display font-black text-lg">{t("welcome_title")}</p></div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{t("welcome_text")}</p>
            <Button onClick={() => nav("/app/profile-360")} data-testid="dash-welcome-cta" className="w-full rounded-full h-12 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold text-sm">
              {t("welcome_cta")} <ArrowRight size={16} className="ml-1.5" />
            </Button>
            <button onClick={() => window.dispatchEvent(new Event("lb-open-tour"))} data-testid="dash-welcome-tour" className="w-full mt-3 text-xs text-[hsl(var(--muted-foreground))] hover:text-white transition-colors">
              {t("welcome_tour")}
            </button>
          </motion.div>
        )}
        {/* Guided journey — PLAN IN PROGRESS (once launched) or YOUR #1 PRIORITY */}
        {/* Cockpit — Diagnostic & Plan (comptes réels, uniquement si un diagnostic existe) */}
        {!demo && diag && diag.priorities?.[0] && (() => {
          const LVL = { critique: "bg-red-500/15 text-red-300 border-red-400/30", eleve: "bg-orange-500/15 text-orange-300 border-orange-400/30", surveiller: "bg-amber-500/15 text-amber-300 border-amber-400/30", opportunite: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30" };
          const p0 = diag.priorities[0];
          const acts = diagPlan ? ["30", "60", "90"].flatMap((h) => diagPlan.horizons?.[h] || []) : [];
          const cnt = acts.reduce((m, a) => { m[a.status] = (m[a.status] || 0) + 1; return m; }, {});
          const ev = Array.isArray(diag.evolution) ? diag.evolution : [];
          const trend = ev.length ? (ev.filter((e) => e.trend === "ameliore").length >= ev.filter((e) => e.trend === "degrade").length ? "ameliore" : "degrade") : null;
          return (
            <motion.div variants={item} className="lb-card p-4" data-testid="dash-cockpit">
              <div className="flex items-center gap-2 mb-3"><Brain size={16} className="text-neon" /><p className="font-semibold text-sm uppercase tracking-wide">{t("dash_cockpit")}</p></div>
              <p className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">{t("dash_priority_now")}</p>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold ${LVL[p0.niveau] || LVL.surveiller}`}>{t(`lvl_${p0.niveau}`)}</span>
                <p className="font-semibold text-sm">{p0.titre}</p>
              </div>
              {acts.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3" data-testid="dash-cockpit-actions">
                  {[["todo", cnt.todo || 0], ["progress", cnt.progress || 0], ["done", cnt.done || 0], ["reevaluate", cnt.reevaluate || 0]].filter(([, n]) => n > 0).map(([s, n]) => (
                    <span key={s} className="text-xs px-2.5 py-1 rounded-lg bg-white/[0.04] border border-white/10">{n} · {t(`st_${s}`)}</span>
                  ))}
                </div>
              )}
              {trend && (
                <p className={`text-xs mb-3 inline-flex items-center gap-1 ${trend === "ameliore" ? "text-emerald-400" : "text-red-400"}`}>
                  {trend === "ameliore" ? <TrendingUp size={13} /> : <TrendingDown size={13} />} {t("diag_evolution")} : {t(`trend_${trend}`)}
                </p>
              )}
              <Button onClick={() => nav("/app/diagnostic")} data-testid="dash-cockpit-cta" className="w-full rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold text-sm">
                {t("p360_see_diag")} <ArrowUpRight size={15} className="ml-1" />
              </Button>
            </motion.div>
          );
        })()}
        {hasPlan ? (
          <motion.div variants={item} className="beam p-[1px]" data-testid="plan-in-progress">
            <div className="rounded-[1.2rem] bg-[var(--lb-card)] p-5">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <p className="inline-flex items-center gap-2 text-xs font-bold tracking-wide text-neon"><Rocket size={15} /> {J.planTitle}</p>
                <button onClick={() => nav("/app/calendar")} className="text-xs text-neon flex items-center gap-1 hover:gap-2 transition-all" data-testid="plan-see-calendar">{J.plannedActions} <ArrowUpRight size={14} /></button>
              </div>
              {planObjective && <p className="text-sm font-medium mb-3">{J.objective} : <span className="text-white/90">{planObjective}</span></p>}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{J.progress}</p>
                  <p className="font-display font-black text-xl mt-0.5 text-neon">{planProgress}%</p>
                </div>
                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{J.plannedActions}</p>
                  <p className="font-display font-black text-xl mt-0.5">{scope.length}</p>
                </div>
                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{J.nextAction}</p>
                  <p className="text-xs font-semibold mt-1.5 line-clamp-2 leading-tight">{nextMission?.title || J.none}</p>
                </div>
              </div>
              <Button data-testid="plan-continue-cta" onClick={() => nav("/app/plan")} className="w-full mt-4 rounded-full h-12 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">
                <Rocket size={16} className="mr-1.5" /> {J.continuePlan}
              </Button>
            </div>
          </motion.div>
        ) : priority ? (
          <motion.div variants={item} className="beam p-[1px]" data-testid="priority-hero">
            <div className="rounded-[1.2rem] bg-[var(--lb-card)] p-5">
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">{J.analyzedLead(bizName)}</p>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-7 h-7 rounded-lg bg-neon/15 text-neon flex items-center justify-center shrink-0"><Target size={16} /></span>
                <p className="text-xs font-bold tracking-wide text-neon">{J.priorityLabel}</p>
              </div>
              <h2 className="font-display font-black text-xl leading-tight mb-2" data-testid="priority-title">{priority.title}</h2>
              <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed mb-4" data-testid="priority-why">{priority.why}</p>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{J.impact}</p>
                  <p className="text-xs font-semibold text-neon mt-0.5 leading-tight">{priority.impactShort}</p>
                </div>
                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{J.effort}</p>
                  <p className="text-xs font-semibold mt-0.5">{priority.effort}</p>
                </div>
                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{J.delay}</p>
                  <p className="text-xs font-semibold mt-0.5">{priority.delay}</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button data-testid="priority-act-cta" onClick={goActWithAI} className="flex-1 rounded-full h-12 min-h-[48px] bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">
                  <Sparkles size={17} className="mr-1.5" /> ✨ {J.createPlanCta}
                </Button>
                <Button data-testid="priority-see-others" onClick={scrollToOpportunities} variant="outline" className="rounded-full h-12 px-4 border-white/10 bg-white/5 text-sm">
                  {J.seeOthers}
                </Button>
              </div>
            </div>
          </motion.div>
        ) : null}

        {/* Profil 360° — complétude (second niveau de lecture, sous la priorité) */}
        {!demo && prof360 != null && !(prof360 === 0 && !diag) && (
          <motion.div variants={item} className="lb-card p-4" data-testid="profile360-card">
            <div className="flex items-center gap-2 mb-2"><Building2 size={16} className="text-neon" /><p className="font-semibold text-sm">PROFIL ENTREPRISE 360°</p></div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Profil complété à {prof360}%</p>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-3"><div className="h-full bg-gradient-to-r from-primary to-neon transition-all" style={{ width: `${prof360}%` }} /></div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Plus votre profil est complet, plus les analyses et recommandations de LocalBoost seront précises.</p>
            {prof360 < 50 && <p className="text-xs text-amber-400 mb-3" data-testid="profile360-alert">Votre profil est encore incomplet. Complétez-le pour obtenir des analyses plus précises.</p>}
            <Button onClick={() => nav("/app/profile-360")} data-testid="profile360-card-cta" className="w-full rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold text-sm">
              {prof360 === 0 ? "Compléter mon profil" : prof360 >= 100 ? "Mettre à jour mon profil" : "Continuer mon profil"}
            </Button>
          </motion.div>
        )}
        {/* Optional commercial KPIs — shown only when provided (real user) or in demo */}
        {(() => {
          const o = data.onboarding || {};
          const cells = [
            o.revenue_monthly && { label: "CA mensuel", value: `${Number(o.revenue_monthly).toLocaleString("fr-FR")} €`, source: "saisi" },
            o.gross_margin && { label: "Marge", value: `${o.gross_margin} %`, source: "saisi" },
            o.avg_basket && { label: "Panier moyen", value: `${o.avg_basket} €`, source: "saisi" },
          ].filter(Boolean);
          if (!cells.length) return null;
          return (
            <motion.div variants={item} className="grid grid-cols-3 gap-3" data-testid="dash-kpis">
              {cells.map((c, i) => (
                <div key={i} className="lb-card p-4 text-center">
                  <p className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{c.label}</p>
                  <p className="font-display font-bold text-lg mt-1">{c.value}</p>
                  {c.source && (
                    <div className="mt-1.5 flex justify-center">
                      <span data-testid={`dash-kpi-prov-${c.source}`} className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10 text-[hsl(var(--muted-foreground))]">{t(PROV_LABEL_KEY[c.source])}</span>
                    </div>
                  )}
                </div>
              ))}
            </motion.div>
          );
        })()}

        <motion.div variants={item} className="beam p-[1px]">
          <div className="rounded-[1.2rem] bg-[var(--lb-card)] p-5 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-neon/15 flex items-center justify-center text-neon shrink-0"><Sparkles size={20} /></div>
              <div>
                <p className="font-display font-bold text-lg flex items-center gap-1.5"><span className="neon-text">{`${scores.opportunities} opportunités détectées`}</span><HelpHint k="opportunities" side="bottom" /></p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="opp-breakdown">{`${Math.min(3, scores.opportunities)} prioritaires maintenant · ${Math.max(0, scores.opportunities - Math.min(3, scores.opportunities))} autres à explorer`}</p>
              </div>
            </div>
            <button onClick={() => nav("/app/command")} className="min-h-[44px] sm:min-h-0 text-sm text-neon flex items-center gap-1 hover:gap-2 transition-all shrink-0" data-testid="dash-to-command">Explorer <ArrowUpRight size={16} /></button>
          </div>
        </motion.div>

        {/* Mission reminders (today / tomorrow) */}
        <motion.div variants={item}><MissionReminders /></motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* North star */}
          <Card testid="dash-score" className="lg:col-span-1 flex flex-col items-center justify-center">
            <span className="text-sm text-[hsl(var(--muted-foreground))] self-start mb-2 flex items-center gap-1">Business Score <HelpHint k="businessScore" side="right" /></span>
            <ScoreRing value={scores.global} size={190} />
            {scoreDelta != null ? (
              <div className="mt-4 flex items-center gap-2 text-xs text-emerald-400"><TrendingUp size={14} /> {scoreDelta >= 0 ? "+" : ""}{scoreDelta} depuis la 1re analyse</div>
            ) : (
              <div className="mt-4 text-xs text-[hsl(var(--muted-foreground))]">Score initial · relancez une analyse pour suivre l'évolution</div>
            )}
            {weak && (
              <div className="mt-4 w-full rounded-xl bg-white/[0.03] border border-white/5 p-3 text-left" data-testid="score-explainer">
                <p className="text-[11px] font-semibold text-neon mb-1">{J.scoreWhy}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">{J.scoreWeak(weak.label, weak.value)} {J.scoreHow}</p>
              </div>
            )}
          </Card>

          {/* Radar sub-scores */}
          <Card testid="dash-subscores" className="lg:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[hsl(var(--muted-foreground))]">Sous-scores</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} outerRadius="72%">
                    <PolarGrid stroke="rgba(255,255,255,0.08)" />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: "#94A3B8", fontSize: 10 }} />
                    <Radar dataKey="value" stroke="#00E5FF" fill="#6366F1" fillOpacity={0.35} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2.5">
                {Object.entries(scores.subs).map(([k, v]) => (
                  <div key={k}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-[hsl(var(--muted-foreground))]">{SUB_LABELS[k]}</span><span className="font-semibold">{v}</span></div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <motion.div className="h-full rounded-full bg-gradient-to-r from-primary to-neon" initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ duration: 1, delay: 0.3 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Since your last analysis — real per-axis comparison */}
        {canCompare && (
          <Card testid="dash-evolution-compare">
            <h3 className="font-display font-bold text-lg mb-3">Depuis votre dernière analyse</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {compareRows.map((r) => (
                <div key={r.k} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/5" data-testid={`compare-${r.k}`}>
                  <div>
                    <p className="text-sm font-medium">{r.label}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">{r.from} → {r.to}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${r.delta > 0 ? "bg-emerald-500/15 text-emerald-400" : r.delta < 0 ? "bg-red-500/15 text-red-400" : "bg-white/5 text-[hsl(var(--muted-foreground))]"}`}>
                    {r.delta > 0 ? `+${r.delta}` : r.delta < 0 ? `${r.delta}` : "stable"}
                  </span>
                </div>
              ))}
            </div>
            {topGain && topGain.delta > 0 && (
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3">Vos actions sur « {topGain.label} » semblent aller dans la bonne direction.</p>
            )}
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Priority actions */}
          <Card testid="dash-actions" className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg flex items-center gap-1.5">Actions prioritaires <HelpHint k="priorityActions" side="right" /></h3>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">{`${doneCount}/${scope.length} terminées`}</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mb-4">
              <motion.div className="h-full bg-gradient-to-r from-emerald-500 to-neon" animate={{ width: `${planProgress}%` }} />
            </div>
            <div className="space-y-2">
              {actions.length === 0 && (
                <div className="text-center py-8" data-testid="actions-empty">
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">Aucune mission active pour le moment.</p>
                  <button onClick={() => nav("/app/command")} className="text-sm text-neon" data-testid="actions-empty-cta">Demandez un plan à l'IA →</button>
                </div>
              )}
              {actions.map((a) => {
                const status = a.status || (a.done ? "done" : "todo");
                const st = STATUS_META[status];
                const rowStyle = status === "done"
                  ? "bg-emerald-500/5 border-emerald-500/20"
                  : status === "progress"
                  ? "bg-[#00E5FF]/[0.06] border-[#00E5FF]/30"
                  : "bg-white/[0.02] border-white/5 hover:border-primary/30";
                return (
                  <div key={a.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${rowStyle}`} data-testid={`action-${a.id}`}>
                    <span role="button" tabIndex={0} onClick={() => toggleAction(a.id)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleAction(a.id); } }}
                      data-testid={`action-check-${a.id}`}
                      className="inline-flex items-center justify-center shrink-0 cursor-pointer w-11 h-11 -m-3.5 sm:w-4 sm:h-4 sm:m-0">
                      <Checkbox checked={a.done} tabIndex={-1} className="pointer-events-none data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500" />
                    </span>
                    <button onClick={() => setSelected(a)} className="flex-1 min-w-0 text-left" data-testid={`action-open-${a.id}`}>
                      <div className="flex items-center gap-2">
                        {a.priority && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: PRIO_COLOR[a.priority] || "#F59E0B" }} />}
                        <p className={`text-sm font-medium truncate ${a.done ? "line-through text-[hsl(var(--muted-foreground))]" : ""}`}>{a.title}</p>
                      </div>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{a.channel} · <span className="text-neon">{a.impact}</span></p>
                    </button>
                    {a.isMission && st && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full shrink-0" style={{ background: `${st.color}22`, color: st.color }} data-testid={`action-status-${a.id}`}><st.icon size={10} /><span className="hidden xs:inline sm:inline">{st.label}</span></span>
                    )}
                    <ChevronRight size={16} className="text-[hsl(var(--muted-foreground))] shrink-0" />
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Evolution */}
          <Card testid="dash-evolution">
            <h3 className="font-display font-bold text-lg mb-1">Évolution du score</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{history ? "6 derniers mois" : "Historique"}</p>
            {history ? (
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={history}>
                    <defs><linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366F1" stopOpacity={0.5} /><stop offset="100%" stopColor="#6366F1" stopOpacity={0} /></linearGradient></defs>
                    <XAxis dataKey="month" tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#121218", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                    <Area type="monotone" dataKey="score" stroke="#00E5FF" strokeWidth={2} fill="url(#scoreGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-40 flex flex-col items-center justify-center text-center" data-testid="evolution-empty">
                <p className="text-sm mb-1">{NR}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Disponible après plusieurs analyses.</p>
              </div>
            )}
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Reputation */}
          <Card testid="dash-reputation" className="cursor-pointer" >
            <div onClick={() => nav("/app/reputation")}>
              <div className="flex items-center gap-2 text-yellow-400 mb-2"><Star size={18} fill="currentColor" /><span className="font-display font-black text-2xl">{rep ? <AnimatedNumber value={rep.average} decimals={1} /> : "—"}</span></div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{rep ? `Note moyenne · ${rep.count} avis` : "Note moyenne"}</p>
              {rep ? <p className="text-xs text-emerald-400 mt-2">+{rep.evolution} ce mois</p> : <p className="text-xs mt-2">{NR}</p>}
            </div>
          </Card>
          {/* Streak / gamification */}
          <Card testid="dash-streak">
            <div className="flex items-center gap-2 text-orange-400 mb-2"><Flame size={18} /><span className="font-display font-black text-2xl">{gam ? `${gam.streak} j` : "—"}</span></div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Série d'activité</p>
            {gam ? <p className="text-xs text-neon mt-2">{gam.level}</p> : <p className="text-xs mt-2">{NR}</p>}
          </Card>
          {/* Monthly progression */}
          <Card testid="dash-monthly">
            <div className="flex items-center gap-2 text-emerald-400 mb-2"><Zap size={18} /><span className="font-display font-black text-2xl">{planProgress}%</span></div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Progression du plan</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">{`${doneCount}/${scope.length} missions terminées`}</p>
          </Card>
          {/* Next calendar */}
          <Card testid="dash-nextcal" className="cursor-pointer">
            <div onClick={() => nav("/app/calendar")}>
              <div className="flex items-center gap-2 text-neon mb-2"><Calendar size={18} /><span className="font-display font-black text-2xl">{plannedCount}</span></div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Actions planifiées</p>
              <p className="text-xs text-neon mt-2">Voir le calendrier →</p>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Upcoming */}
          <Card testid="dash-upcoming" className="lg:col-span-2">
            <h3 className="font-display font-bold text-lg mb-3">Prochaines publications & animations</h3>
            <div className="space-y-2">
              {upcomingSorted.length === 0 && (
                <div className="text-center py-8" data-testid="upcoming-empty">
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">Aucune publication ni animation planifiée.</p>
                  <button onClick={() => nav("/app/command")} className="inline-flex items-center min-h-[44px] sm:min-h-0 text-sm text-neon mt-2" data-testid="upcoming-empty-cta">Lancez un plan pour les programmer →</button>
                </div>
              )}
              {upcomingSorted.map((e) => (
                <div key={e.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5">
                  <div className="w-2 h-8 rounded-full" style={{ background: CAL_COLORS[e.type] }} />
                  <div className="flex-1"><p className="text-sm font-medium">{e.title}</p><p className="text-xs text-[hsl(var(--muted-foreground))] capitalize">{e.type} · {e.channel}</p></div>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">J{e.day}</span>
                </div>
              ))}
            </div>
          </Card>
          {/* Recent activity + badges */}
          <Card testid="dash-recent">
            <h3 className="font-display font-bold text-lg mb-3">Activité récente</h3>
            <div className="space-y-3 mb-4">
              {recent.length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="recent-empty">Aucune activité pour l'instant.</p>}
              {recent.map((r, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-neon mt-1.5" />
                  <div><p className="text-sm">{r.text}</p><p className="text-xs text-[hsl(var(--muted-foreground))]">{r.time}</p></div>
                </div>
              ))}
            </div>
            {gam && gam.badges && (
              <div className="flex gap-2 flex-wrap pt-3 border-t border-white/5">
                {gam.badges.slice(0, 4).map((b, i) => (
                  <div key={i} title={b.name} className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center text-neon"><Award size={16} /></div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </motion.div>

      {/* Mission detail modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="glass border-white/10 w-[calc(100%-2rem)] max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="mission-detail">
          {selected && (() => {
            const st = STATUS_META[selected.status || (selected.done ? "done" : "todo")];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 pr-6">
                    {selected.priority && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: PRIO_COLOR[selected.priority] || "#F59E0B" }} />}
                    {selected.title}
                  </DialogTitle>
                  <DialogDescription className="sr-only">Détail de la mission et gestion du statut</DialogDescription>
                </DialogHeader>
                {selected.description && <p className="text-sm text-[hsl(var(--muted-foreground))]">{selected.description}</p>}
                {selected.why && (
                  <div className="rounded-xl bg-neon/[0.06] border border-neon/20 p-3 mt-2" data-testid="mission-why">
                    <p className="text-[11px] font-semibold text-neon mb-1">Pourquoi cette action ?</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">{selected.why}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 my-2">
                  <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3"><p className="text-[10px] text-[hsl(var(--muted-foreground))] mb-0.5">Canal</p><p className="text-sm font-medium">{selected.channel || "—"}</p></div>
                  <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3"><p className="text-[10px] text-[hsl(var(--muted-foreground))] mb-0.5">Impact attendu</p><p className="text-sm font-medium text-neon">{selected.impact || "—"}</p></div>
                  {selected.priority && <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3"><p className="text-[10px] text-[hsl(var(--muted-foreground))] mb-0.5">Priorité</p><p className="text-sm font-medium capitalize" style={{ color: PRIO_COLOR[selected.priority] }}>{selected.priority}</p></div>}
                  {typeof selected.day_offset === "number" && <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3"><p className="text-[10px] text-[hsl(var(--muted-foreground))] mb-0.5">Échéance</p><p className="text-sm font-medium">{`Dans ${selected.day_offset} j`}</p></div>}
                </div>
                {st && <p className="text-xs mb-2">Statut actuel : <span style={{ color: st.color }}>{st.label}</span></p>}
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(STATUS_META).map(([key, meta]) => (
                    <button key={key} data-testid={`status-${key}`} onClick={() => changeStatus(selected.id, key)}
                      className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border text-xs transition-colors ${(selected.status || (selected.done ? "done" : "todo")) === key ? "border-2" : "border-white/10 bg-white/[0.02] hover:bg-white/5"}`}
                      style={(selected.status || (selected.done ? "done" : "todo")) === key ? { borderColor: meta.color, background: `${meta.color}18`, color: meta.color } : {}}>
                      <meta.icon size={16} /> {meta.label}
                    </button>
                  ))}
                </div>
                {selected.needs_content && (
                  <Button data-testid="mission-prepare-content" onClick={() => prepareContent(selected)} className="w-full mt-3 rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn">
                    <PenTool size={15} className="mr-1.5" /> {ctaLabel(selected)}
                  </Button>
                )}
                {!selected.needs_content && canCreate(selected) && (
                  <Button data-testid="mission-create-action" onClick={() => prepareContent(selected)} className="w-full mt-3 rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn">
                    <PenTool size={15} className="mr-1.5" /> {ctaLabel(selected)}
                  </Button>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
